// next components
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState, useRef } from 'react'

// components
import PrimaryLayout from 'components/Layout/PrimaryLayout'
import TopRatedArtistsSection from 'components/pages/Home/TopRatedArtistsSection'
import MarketplaceReleaseSection from 'components/pages/Home/MarketplaceReleaseSection'
import TrendingCollectionSection from 'components/pages/Home/TrendingCollectionSection'
import LiveAuctionSection from 'components/pages/Home/LiveAuctionSection'

// images
import SolonaLogoIcon from 'assets/icons/solana-logo-44x50.svg?component'
import ArrowIcon from 'assets/icons/arrow-right-13x10.svg?component'

// custom functions
import { useDetectRouterPath, useViewport } from 'utils'

// third party components
import { useSelector } from 'react-redux'
import { setColorTheme } from 'utils/helpers'

import styles from './index.module.scss'
import LimitedEditionSection from 'components/pages/Home/LimitedEditionSection'
import OpenEditionSection from 'components/pages/Home/OpenEditionSection'
import FeaturedStoreSection from 'components/pages/Home/FeaturedStoreSection'
import FeaturedArtworkSection from 'components/pages/Home/FeaturedArtworkSection'

// api
import dashboardAPI from 'apis/dashboard'
import StudioHeroSection from 'components/pages/Home/StudioHeroSection'
import ShopMerchBrandSection from 'components/pages/Home/ShopMerchBrandSection'
import HomeStudioReleaseSection from 'components/pages/Home/HomeStudioReleaseSection'
import MerchPlatformSection from 'components/pages/Home/MerchPlatformSection'
import ShopPhysicalSection from 'components/pages/Home/ShopPhysicalSection'
import CollectionSpotSection from 'components/pages/Home/CollectionSpotSection'
import MerchCreatorSection from 'components/pages/Home/MerchCreatorSection'

// utils
import { isValidUrl } from 'utils/ipfs'
import SpotlightSection from 'components/pages/Home/SpotlightSection'
import TrendingBrandSection from 'components/pages/Home/TrendingBrandSection'
import UpcomingEventSection from 'components/pages/Home/UpcomingEventSection'
import TrendingCreatorSection from 'components/pages/Home/TrendingCreatorSection'

let pageTitle = 'Foster Marketplace'
let pageImageUrl = 'https://foster-marketplace-dev.s3.us-east-2.amazonaws.com/preview-image.webp'
let pageDescription = 'A boutique marketplace for indie-crafted art, bespoke apparel, and timeless collectibles.'

const Home = () => {
  const viewport = useViewport()
  const router = useRouter()
  useDetectRouterPath(useRouter())

  const lightDarkMode = useSelector(state => (state as any).lightMode)

  // const [limitedEditionList, setLimitedEditionList] = useState<any>([])
  const [liveEditionList, setLiveEditionList] = useState<any>([])
  const [openEditionList, setOpenEditionList] = useState<any>([])
  const [visibleBoundary, setVisibleBoundary] = useState(false)
  const [isEditionLoaded, setIsEditionLoaded] = useState(false)
  const [loadedStudioRelease, setLoadedStudioRelease] = useState(false)
  const [hideEventSection, setHideEventSection] = useState(false)
  const [sectionVisibility, setSectionVisibility] = useState({
    showLiveAndShop: false, // Controls LiveAuctionSection and ShopMerchBrandSection
    showMarketplace: false, // Controls MarketplaceReleaseSection
  })

  // Create refs for trigger elements
  const limitedEditionRef = useRef<HTMLDivElement>(null)

  // Intersection Observer setup
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === limitedEditionRef.current) {
            setSectionVisibility(prev => ({ ...prev, showLiveAndShop: true }))
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    if (limitedEditionRef.current) {
      observer.observe(limitedEditionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setColorTheme(lightDarkMode)

    dashboardAPI.getListedEditions('', '', 1, 15).then(async response => {
      if (response) {
        let editionList = []
        if (Array.isArray(response.limitedEditions)) {
          const limitedEditionList = []
          for (const item of response.limitedEditions) {
            if (item.maxSupply - item.currentSupply <= 0) {
              continue
            }

            if (item.coverImageUrl && item.coverImageUrl.includes('ipfs.io')) {
              try {
                const hasProviders = await isValidUrl(item.coverImageUrl)
                if (hasProviders) {
                  limitedEditionList.push(item)
                }
              } catch (error) {
                console.warn('Error checking IPFS availability for item:', item.coverImageUrl, error)
                limitedEditionList.push(item)
              }
            } else {
              limitedEditionList.push(item)
            }
          }
          editionList = limitedEditionList.slice(0, 10)
        }
        if (Array.isArray(response.openEditions)) {
          setLiveEditionList([...response.openEditions.slice(0, 5), ...editionList.slice(0, 5)])
          setOpenEditionList(response.openEditions)
        } else {
          setLiveEditionList(editionList)
        }
      }
      setIsEditionLoaded(true)
    }),
      error => {
        console.log(error)
        setIsEditionLoaded(true)
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    // <MaintenancePage />

    <div className="w-full">
      {/* <div className="w-full flex justify-center">
        <div className="max-w-[1920px] w-full px-4 lg:px-0">
          <MainSection />
        </div>
      </div> */}

      <div className={styles.featuredStoreSection}>
        <div className="g-container w-full">
          <StudioHeroSection />
        </div>
      </div>

      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <MerchCreatorSection />
        </div>
      </div>

      <div className={styles.featuredStoreSection}>
        <div className="w-full">
          <FeaturedStoreSection />
        </div>
      </div>

      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <ShopPhysicalSection />
        </div>
      </div>

      <div className={`${styles.featuredStoreSection} `}>
        <div className="lg:g-container max-w-[100dvw]">
          <HomeStudioReleaseSection onLoaded={() => setLoadedStudioRelease(true)} />
        </div>
      </div>

      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <SpotlightSection />
        </div>
      </div>

      <div className={styles.trendingBrandSection}>
        <div className="w-full">
          <TrendingBrandSection />
        </div>
      </div>
      {!hideEventSection && (
        <div className={styles.trendingBrandSection}>
          <div className="w-full">
            <UpcomingEventSection hideSection={() => setHideEventSection(true)} />
          </div>
        </div>
      )}
      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <FeaturedArtworkSection />
        </div>
      </div>

      <div className={styles.limitEditionSection}>
        <div className={`${viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '' : 'g-container'} w-full`}>
          <LimitedEditionSection category={''} isEditionLoaded={isEditionLoaded} editionList={liveEditionList} />
        </div>
      </div>

      {/* <div className={styles.editionSection}>
        <div className={`${viewport === 'xs' ? '' : 'g-container'} w-full`}>
          <OpenEditionSection category={''} isEditionLoaded={isEditionLoaded} editionList={openEditionList} />
        </div>
      </div> */}

      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <CollectionSpotSection />
        </div>
      </div>

      <div ref={limitedEditionRef} className={`${styles.liveAuctionBG} `}>
        <div className={`${viewport === 'xs' ? '' : 'g-container'} w-full`}>
          <LiveAuctionSection category={''} fromPage={'home'} />
        </div>
      </div>

      {/* {loadedStudioRelease && <div ref={shopMerchBrandRef} className="h-[1px] w-full" />} */}

      {/* {sectionVisibility.showLiveAndShop && (
        <div className={`${styles.pushingBoundary}`}>
          <div className="g-container">
            <ShopMerchBrandSection />
          </div>
        </div>
      )} */}

      {false && visibleBoundary && (
        <div className={styles.pushingBoundary}>
          <div className="g-container w-full flex justify-center items-center">
            <div className="lg:flex hidden gap-[28px]">
              <div>
                <SolonaLogoIcon />
              </div>
              <div className="">
                <div className={styles.pushTitle}>Pushing the boundaries of Digital Art on Solana</div>
                <div className={styles.pushText}>Home to creators, collectors, and curators around the world</div>
              </div>
              <div className={styles.buyButton} onClick={() => router.push('/explore')}>
                <div>Explore Art</div>
                <div className={styles.buttonIcon}>
                  <ArrowIcon className={styles.arrowIcon} />
                </div>
              </div>
            </div>
            {/* Mobile */}
            <div className="lg:hidden block gap-[28px]">
              <div className="w-full flex justify-center">
                <div>
                  <SolonaLogoIcon />
                </div>
              </div>
              <div className="mt-[15px]">
                <div className={styles.pushTitle}>Pushing the boundaries of Digital Art on Solana</div>
                <div className={styles.pushText}>
                  A boutique marketplace for indie-crafted art, bespoke apparel, and timeless collectibles.
                </div>
              </div>
              <div className="w-full flex justify-center mt-[27px]">
                <div className={styles.buyButton} onClick={() => router.push('/explore')}>
                  <div>Explore Art</div>
                  <div className={styles.buttonIcon}>
                    <ArrowIcon className={styles.arrowIcon} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* {sectionVisibility.showLiveAndShop && (
        <div className={`${styles.otherBG} `}>
          {viewport === 'xs' ? (
            <div className="w-full">
              <TopRatedArtistsSection />
            </div>
          ) : (
            <div className="g-container">
              <TopRatedArtistsSection />
            </div>
          )}
        </div>
      )} */}

      <div className={styles.shopPhysicalSection}>
        <div className="w-full">
          <TrendingCreatorSection />
        </div>
      </div>

      {sectionVisibility.showLiveAndShop && (
        <div className={`${styles.featuredStoreSection} `}>
          <div className="g-container">
            <MarketplaceReleaseSection category={''} setVisibleBoundary={setVisibleBoundary} />
          </div>
        </div>
      )}

      {/*sectionVisibility.showLiveAndShop && (
        <div className={`${styles.collectionSection} `}>
          <div className="g-container">
            <TrendingCollectionSection category={''} loop={true} />
          </div>

          <div className="g-container lg:pb-[140px] lg:flex hidden">
            <MerchPlatformSection />
          </div>

          <div className="w-full h-[100px] lg:hidden" />
        </div>
      )*/}
      {/* <div className={styles.shopMerchSection}>
        <div className="g-container">
          <ShopMerchSection />
        </div>
      </div> */}
    </div>
  )
}

export default Home

Home.getLayout = function getLayout(page) {
  const head = (
    <Head>
      <title>{pageTitle}</title>

      <meta name="description" content={pageDescription} />

      <meta name="og:title" content={pageTitle} />
      <meta name="og:description" content={pageDescription} />
      <meta name="og:image" content={pageImageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImageUrl} />

      <link rel="icon" href="/logo.png" />
    </Head>
  )
  return <PrimaryLayout head={head}>{page}</PrimaryLayout>
}

export function getStaticProps() {
  return { props: {} }
}
